import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Progress } from '../components/ui/progress';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Palette, FileText, Video, Mic, BookOpen, Code, Layers, Search,
  Plus, Edit, Trash2, Eye, Calendar, TrendingUp, Loader2, Sparkles,
  Target, BarChart3, ExternalLink, Image, Mail, Share2, Hash,
  Link2, Filter, Clock, CheckCircle2, RefreshCw, Lightbulb, Wand2,
  AlertCircle, ThumbsUp, MessageSquare, Globe, Instagram, Twitter, Linkedin, Facebook,
  Users, ListTodo, CheckSquare, Play, Pause, Settings, Copy, Zap, PenTool
} from 'lucide-react';
import { contentAPI, campaignsAPI, brandVoiceAPI, aiToolsAPI, usersAPI, omniChannelAPI, marketingTasksAPI } from '../api';

const CONTENT_TYPES = [
  { value: 'landing_page', label: 'Landing Page', icon: Layers, color: 'bg-blue-100 text-blue-700' },
  { value: 'blog', label: 'Blog Post', icon: FileText, color: 'bg-green-100 text-green-700' },
  { value: 'social_media', label: 'Social Media', icon: Share2, color: 'bg-purple-100 text-purple-700' },
  { value: 'email', label: 'Email Campaign', icon: Mail, color: 'bg-rose-100 text-rose-700' },
  { value: 'video', label: 'Video Script', icon: Video, color: 'bg-amber-100 text-amber-700' },
  { value: 'podcast', label: 'Podcast', icon: Mic, color: 'bg-orange-100 text-orange-700' },
  { value: 'case_study', label: 'Case Study', icon: BookOpen, color: 'bg-teal-100 text-teal-700' },
  { value: 'seo_content', label: 'SEO Content', icon: TrendingUp, color: 'bg-yellow-100 text-yellow-700' },
  { value: 'embed', label: 'Embed/Widget', icon: Code, color: 'bg-slate-100 text-slate-700' },
];

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft', color: 'bg-slate-100 text-slate-700' },
  { value: 'in_review', label: 'In Review', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'approved', label: 'Approved', color: 'bg-blue-100 text-blue-700' },
  { value: 'published', label: 'Published', color: 'bg-green-100 text-green-700' },
  { value: 'archived', label: 'Archived', color: 'bg-gray-100 text-gray-700' }
];

const SOCIAL_PLATFORMS = [
  { value: 'instagram', label: 'Instagram', icon: Instagram, maxLength: 2200 },
  { value: 'twitter', label: 'Twitter/X', icon: Twitter, maxLength: 280 },
  { value: 'linkedin', label: 'LinkedIn', icon: Linkedin, maxLength: 3000 },
  { value: 'facebook', label: 'Facebook', icon: Facebook, maxLength: 63206 },
];

const EMAIL_TYPES = [
  { value: 'newsletter', label: 'Newsletter' },
  { value: 'promotional', label: 'Promotional' },
  { value: 'welcome', label: 'Welcome Series' },
  { value: 'nurture', label: 'Nurture Sequence' },
  { value: 'announcement', label: 'Announcement' },
  { value: 'event', label: 'Event Invitation' },
];

export const MarketingCreativePage = () => {
  const { hasRole, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState([]);
  const [stats, setStats] = useState({});
  const [campaigns, setCampaigns] = useState([]);
  const [brandVoice, setBrandVoice] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showAIStudioDialog, setShowAIStudioDialog] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  const [creating, setCreating] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  
  // Campaign management state
  const [showCampaignDialog, setShowCampaignDialog] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignTab, setCampaignTab] = useState('overview');
  
  // Task management state
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    assigned_to: '',
    due_date: '',
    priority: 'medium',
    content_id: '',
  });
  const [contentTasks, setContentTasks] = useState([]);
  const [aiSuggestingTask, setAiSuggestingTask] = useState(false);  // Bug #9: AI task assistant

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content_type: 'blog',
    body: '',
    excerpt: '',
    meta_title: '',
    meta_description: '',
    keywords: '',
    categories: '',
    tags: '',
    campaign_ids: [],
    featured_image_url: '',
    // Social media specific
    platform: 'instagram',
    hashtags: '',
    // Email specific
    email_type: 'newsletter',
    subject_line: '',
    preview_text: '',
  });

  // AI Studio state
  const [aiInput, setAiInput] = useState({
    idea: '',
    targetAudience: '',
    tone: 'professional',
    keywords: '',
    contentType: 'blog',
    platform: 'instagram',
    length: 'medium',
  });
  const [aiOutput, setAiOutput] = useState(null);
  const [brandVoiceScore, setBrandVoiceScore] = useState(null);
  const [seoAnalysis, setSeoAnalysis] = useState(null);

  useEffect(() => {
    fetchContent();
    fetchStats();
    fetchCampaigns();
    fetchBrandVoice();
    fetchTeamMembers();
    fetchContentTasks();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, search, statusFilter]);
  
  const fetchTeamMembers = async () => {
    try {
      const res = await usersAPI.getTeam();
      setTeamMembers(res.data || []);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
    }
  };
  
  const fetchContentTasks = async () => {
    try {
      // Fetch creative category tasks
      const res = await marketingTasksAPI.getAll({ category: 'creative', include_completed: false, limit: 50 });
      setContentTasks(res.data.tasks || []);
    } catch (error) {
      console.error('Failed to fetch content tasks:', error);
    }
  };

  const fetchContent = async () => {
    try {
      setLoading(true);
      const params = {};
      if (activeTab !== 'all') params.content_type = activeTab;
      if (search) params.search = search;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      
      const res = await contentAPI.getAll(params);
      setContent(res.data?.content || []);
    } catch (error) {
      console.error('Failed to fetch content:', error);
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await contentAPI.getStats();
      setStats(res.data || {});
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const res = await campaignsAPI.getAll({ limit: 100 });
      setCampaigns(res.data?.campaigns || res.data || []);
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
    }
  };

  const fetchBrandVoice = async () => {
    try {
      const res = await brandVoiceAPI.getSettings();
      setBrandVoice(res.data);
    } catch (error) {
      console.error('Failed to fetch brand voice:', error);
    }
  };

  const handleCreateContent = async () => {
    if (!formData.title) {
      toast.error('Title is required');
      return;
    }

    try {
      setCreating(true);
      const data = {
        ...formData,
        keywords: formData.keywords ? formData.keywords.split(',').map(k => k.trim()) : [],
        categories: formData.categories ? formData.categories.split(',').map(c => c.trim()) : [],
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : [],
        campaign_ids: formData.campaign_ids.filter(id => id && id !== 'none'),
      };
      
      const res = await contentAPI.create(data);
      toast.success('Content created successfully');
      setShowCreateDialog(false);
      resetForm();
      fetchContent();
      fetchStats();
      
      // Open detail dialog for editing
      setSelectedContent(res.data);
      setShowDetailDialog(true);
    } catch (error) {
      toast.error('Failed to create content');
    } finally {
      setCreating(false);
    }
  };

  const handleGenerateWithAI = async () => {
    if (!aiInput.idea) {
      toast.error('Please enter an idea or topic');
      return;
    }

    try {
      setGenerating(true);
      setBrandVoiceScore(null);
      setSeoAnalysis(null);

      const res = await contentAPI.generateWithAI({
        content_type: aiInput.contentType,
        topic: aiInput.idea,
        keywords: aiInput.keywords ? aiInput.keywords.split(',').map(k => k.trim()) : [],
        length: aiInput.length,
        tone: aiInput.tone,
        target_audience: aiInput.targetAudience,
        platform: aiInput.platform,
      });
      
      const generated = res.data?.generated_content || {};
      setAiOutput(generated);
      
      // Also analyze Brand Voice alignment
      if (generated.body || generated.content) {
        analyzeGeneratedContent(generated.body || generated.content);
      }
      
      toast.success('Content generated!');
    } catch (error) {
      toast.error('Failed to generate content');
    } finally {
      setGenerating(false);
    }
  };

  const analyzeGeneratedContent = async (text) => {
    try {
      setAnalyzing(true);
      
      // Use real AI API for Brand Voice scoring
      try {
        const brandRes = await aiToolsAPI.scoreBrandVoice({
          content: text,
          brand_voice_settings: brandVoice ? {
            tone: brandVoice.primary_tone,
            personality: brandVoice.brand_personality,
            values: brandVoice.core_values?.join(', '),
            words_to_use: brandVoice.keywords_to_include || [],
            words_to_avoid: brandVoice.words_to_avoid || []
          } : {}
        });
        
        if (brandRes.data?.result) {
          const aiResult = brandRes.data.result;
          setBrandVoiceScore({
            score: aiResult.overall_alignment_score || 70,
            feedback: [
              ...(aiResult.on_brand_elements || []).slice(0, 2).map(e => ({ type: 'positive', text: e })),
              ...(aiResult.off_brand_elements || []).slice(0, 2).map(e => ({ type: 'negative', text: e })),
            ]
          });
        }
      } catch (brandErr) {
        console.error('Brand voice scoring failed:', brandErr);
        // Fallback to local analysis
        const score = calculateBrandVoiceScore(text);
        setBrandVoiceScore(score);
      }
      
      // Use real AI API for SEO analysis
      try {
        const seoRes = await aiToolsAPI.analyzeSEO({
          content: text,
          target_keywords: aiInput.keywords ? aiInput.keywords.split(',').map(k => k.trim()) : [],
          content_type: aiInput.contentType
        });
        
        if (seoRes.data) {
          const { metrics, analysis } = seoRes.data;
          setSeoAnalysis({
            wordCount: metrics?.word_count || 0,
            keywordDensity: Object.fromEntries(
              Object.entries(metrics?.keyword_density || {}).map(([k, v]) => [
                k, { count: v.count, density: v.density, optimal: v.density >= 1 && v.density <= 3 }
              ])
            ),
            suggestions: analysis?.recommendations?.slice(0, 3).map(r => r.action) || [],
            overallScore: analysis?.overall_score || 60
          });
        }
      } catch (seoErr) {
        console.error('SEO analysis failed:', seoErr);
        // Fallback to local analysis
        const seo = analyzeSEO(text, aiInput.keywords);
        setSeoAnalysis(seo);
      }
    } catch (error) {
      console.error('Analysis failed:', error);
    } finally {
      setAnalyzing(false);
    }
  };

  const calculateBrandVoiceScore = (text) => {
    if (!brandVoice || !text) return { score: 0, feedback: [] };
    
    let score = 70; // Base score
    const feedback = [];
    
    // Check for brand keywords
    const keyMessages = brandVoice.key_messages || [];
    const keywordsToInclude = brandVoice.keywords_to_include || [];
    const wordsToAvoid = brandVoice.words_to_avoid || [];
    
    const textLower = text.toLowerCase();
    
    // Positive: Key messages included
    keyMessages.forEach(msg => {
      if (textLower.includes(msg.toLowerCase())) {
        score += 5;
        feedback.push({ type: 'positive', text: `Includes key message: "${msg}"` });
      }
    });
    
    // Positive: Keywords included
    let keywordCount = 0;
    keywordsToInclude.forEach(kw => {
      if (textLower.includes(kw.toLowerCase())) {
        keywordCount++;
      }
    });
    if (keywordCount > 0) {
      score += Math.min(keywordCount * 3, 15);
      feedback.push({ type: 'positive', text: `Contains ${keywordCount} brand keywords` });
    }
    
    // Negative: Words to avoid
    let avoidedCount = 0;
    wordsToAvoid.forEach(word => {
      if (textLower.includes(word.toLowerCase())) {
        avoidedCount++;
        score -= 5;
      }
    });
    if (avoidedCount > 0) {
      feedback.push({ type: 'negative', text: `Contains ${avoidedCount} words to avoid` });
    }
    
    // Check tone alignment
    const toneWords = {
      professional: ['expert', 'solution', 'achieve', 'optimize', 'strategic'],
      friendly: ['you', 'your', 'let\'s', 'together', 'help'],
      authoritative: ['proven', 'research', 'studies show', 'data', 'results'],
      playful: ['fun', 'exciting', 'amazing', 'awesome', 'love'],
    };
    
    const primaryTone = brandVoice.primary_tone || 'professional';
    const toneMatches = (toneWords[primaryTone] || []).filter(w => textLower.includes(w));
    if (toneMatches.length > 0) {
      score += toneMatches.length * 2;
      feedback.push({ type: 'positive', text: `Matches ${primaryTone} tone` });
    }
    
    return { score: Math.min(Math.max(score, 0), 100), feedback };
  };

  const analyzeSEO = (text, keywords) => {
    if (!text) return null;
    
    const analysis = {
      wordCount: text.split(/\s+/).length,
      keywordDensity: {},
      suggestions: [],
    };
    
    // Check keyword density
    const keywordList = keywords ? keywords.split(',').map(k => k.trim().toLowerCase()) : [];
    const textLower = text.toLowerCase();
    const wordCount = analysis.wordCount;
    
    keywordList.forEach(kw => {
      if (!kw) return;
      const regex = new RegExp(kw, 'gi');
      const matches = textLower.match(regex) || [];
      const density = (matches.length / wordCount) * 100;
      analysis.keywordDensity[kw] = {
        count: matches.length,
        density: density.toFixed(2),
        optimal: density >= 1 && density <= 3,
      };
    });
    
    // Suggestions
    if (wordCount < 300) {
      analysis.suggestions.push('Consider adding more content (300+ words recommended for SEO)');
    }
    
    if (wordCount > 2500) {
      analysis.suggestions.push('Content is quite long - consider breaking into multiple pieces');
    }
    
    keywordList.forEach(kw => {
      const data = analysis.keywordDensity[kw];
      if (data && data.count === 0) {
        analysis.suggestions.push(`Target keyword "${kw}" not found - consider including it`);
      } else if (data && parseFloat(data.density) > 3) {
        analysis.suggestions.push(`Keyword "${kw}" may be overused (${data.density}% density)`);
      }
    });
    
    return analysis;
  };

  const applyAIContent = () => {
    if (!aiOutput) return;
    
    setFormData(prev => ({
      ...prev,
      title: aiOutput.title || prev.title,
      body: aiOutput.body || aiOutput.content || prev.body,
      excerpt: aiOutput.meta_description || aiOutput.excerpt || prev.excerpt,
      meta_description: aiOutput.meta_description || prev.meta_description,
      subject_line: aiOutput.subject_line || prev.subject_line,
      hashtags: aiOutput.hashtags?.join(' ') || prev.hashtags,
    }));
    
    setShowAIStudioDialog(false);
    setShowCreateDialog(true);
    toast.success('AI content applied to form');
  };

  const handleDeleteContent = async (id) => {
    if (!window.confirm('Are you sure you want to archive this content?')) return;
    
    try {
      await contentAPI.delete(id);
      toast.success('Content archived');
      fetchContent();
      fetchStats();
    } catch (error) {
      toast.error('Failed to archive content');
    }
  };

  const handleAnalyzeContent = async (id) => {
    try {
      const res = await contentAPI.analyzeWithAI(id);
      toast.success(`Analysis complete: Brand Voice Score ${res.data.brand_voice_score}%`);
      fetchContent();
    } catch (error) {
      toast.error('Failed to analyze content');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content_type: 'blog',
      body: '',
      excerpt: '',
      meta_title: '',
      meta_description: '',
      keywords: '',
      categories: '',
      tags: '',
      campaign_ids: [],
      featured_image_url: '',
      platform: 'instagram',
      hashtags: '',
      email_type: 'newsletter',
      subject_line: '',
      preview_text: '',
    });
  };

  const resetAIStudio = () => {
    setAiInput({
      idea: '',
      targetAudience: '',
      tone: 'professional',
      keywords: '',
      contentType: 'blog',
      platform: 'instagram',
      length: 'medium',
    });
    setAiOutput(null);
    setBrandVoiceScore(null);
    setSeoAnalysis(null);
  };
  
  // Bug #9: AI-assisted task creation
  const handleAISuggestTask = async () => {
    if (!selectedContent && !formData.title && !formData.body) {
      toast.error('Please select content or enter content details first');
      return;
    }
    
    try {
      setAiSuggestingTask(true);
      
      // Use content context to generate task suggestions
      const contextTitle = selectedContent?.title || formData.title || 'Untitled Content';
      const contextBody = selectedContent?.body || formData.body || '';
      const contentType = selectedContent?.content_type || formData.content_type || 'content';
      
      const prompt = `Based on the following ${contentType} content, suggest a creative task:
Title: ${contextTitle}
Content preview: ${contextBody.substring(0, 500)}...

Please suggest:
1. A specific task title (action-oriented)
2. A detailed task description
3. Priority level (low/medium/high)
4. Suggested deadline (number of days from now)`;

      const res = await aiToolsAPI.generateCopy({
        prompt,
        content_type: 'task_suggestion',
        tone: 'professional'
      });
      
      // Parse AI response
      const aiResponse = res.data?.content || res.data?.result || '';
      
      // Try to extract structured data from AI response
      const lines = aiResponse.split('\n').filter(l => l.trim());
      let suggestedTitle = '';
      let suggestedDesc = '';
      let suggestedPriority = 'medium';
      let suggestedDays = 3;
      
      lines.forEach(line => {
        const lower = line.toLowerCase();
        if (lower.includes('title:') || lower.includes('task title')) {
          suggestedTitle = line.split(':').slice(1).join(':').trim().replace(/^["']|["']$/g, '');
        } else if (lower.includes('description:') || lower.includes('task description')) {
          suggestedDesc = line.split(':').slice(1).join(':').trim();
        } else if (lower.includes('priority:')) {
          const pMatch = lower.match(/(low|medium|high)/);
          if (pMatch) suggestedPriority = pMatch[1];
        } else if (lower.includes('deadline:') || lower.includes('days:')) {
          const dMatch = lower.match(/(\d+)/);
          if (dMatch) suggestedDays = parseInt(dMatch[1]);
        }
      });
      
      // If parsing failed, use simpler extraction
      if (!suggestedTitle && lines.length > 0) {
        suggestedTitle = lines[0].replace(/^\d+[\.\)]\s*/, '').replace(/^title[:\s]*/i, '').trim();
      }
      if (!suggestedDesc && lines.length > 1) {
        suggestedDesc = lines.slice(1, 3).join(' ').replace(/^description[:\s]*/i, '').trim();
      }
      
      // Calculate due date
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + suggestedDays);
      
      setTaskForm(prev => ({
        ...prev,
        title: suggestedTitle || `Review and edit ${contextTitle}`,
        description: suggestedDesc || `Complete the following for "${contextTitle}":\n- Review content quality\n- Check brand voice alignment\n- Optimize for target audience\n- Prepare for publication`,
        priority: suggestedPriority,
        due_date: dueDate.toISOString().split('T')[0],
        content_id: selectedContent?.id || ''
      }));
      
      toast.success('AI suggestions applied! Review and adjust as needed.');
    } catch (error) {
      console.error('AI task suggestion failed:', error);
      toast.error('Failed to generate AI suggestions. Please fill in manually.');
    } finally {
      setAiSuggestingTask(false);
    }
  };
  
  // Task Management Functions
  const handleCreateTask = async () => {
    if (!taskForm.title) {
      toast.error('Task title is required');
      return;
    }
    
    try {
      // Create task data for backend
      const taskData = {
        title: taskForm.title,
        description: taskForm.description,
        assigned_to: taskForm.assigned_to || null,
        due_date: taskForm.due_date || null,
        priority: taskForm.priority,
        category: 'creative',  // Set category to creative for Creative Hub tasks
      };
      
      // If linked to content, store association
      if (selectedContent?.id) {
        taskData.content_id = selectedContent.id;
      }
      
      // Create task via backend API
      await marketingTasksAPI.create(taskData);
      toast.success('Task created successfully');
      setShowTaskDialog(false);
      setTaskForm({
        title: '',
        description: '',
        assigned_to: '',
        due_date: '',
        priority: 'medium',
        content_id: '',
      });
      // Refresh tasks list
      fetchContentTasks();
    } catch (error) {
      console.error('Failed to create task:', error);
      toast.error('Failed to create task');
    }
  };
  
  const handleUpdateTaskStatus = async (taskId, newStatus) => {
    try {
      await marketingTasksAPI.update(taskId, { status: newStatus });
      toast.success('Task updated');
      fetchContentTasks();
    } catch (error) {
      console.error('Failed to update task:', error);
      toast.error('Failed to update task');
    }
  };
  
  // Campaign Edit Functions
  const handleEditCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    setShowCampaignDialog(true);
  };
  
  const handleUpdateCampaign = async (updates) => {
    if (!selectedCampaign) return;
    
    try {
      await campaignsAPI.update(selectedCampaign.id, updates);
      toast.success('Campaign updated');
      fetchCampaigns();
      setShowCampaignDialog(false);
    } catch (error) {
      console.error('Failed to update campaign:', error);
      toast.error('Failed to update campaign');
    }
  };
  
  // AI Content Enhancement
  const handleAIEnhanceContent = async (contentToEnhance) => {
    if (!contentToEnhance) {
      toast.error('No content to enhance');
      return;
    }
    
    try {
      setGenerating(true);
      const res = await aiToolsAPI.refineCopy({
        content: contentToEnhance,
        instruction: 'Improve the clarity, engagement, and persuasiveness while maintaining the core message. Add compelling calls-to-action where appropriate.',
        tone: brandVoice?.primary_tone || 'professional',
      });
      
      if (res.data?.refined_content) {
        return res.data.refined_content;
      }
      return contentToEnhance;
    } catch (error) {
      console.error('AI enhancement failed:', error);
      toast.error('Failed to enhance content');
      return contentToEnhance;
    } finally {
      setGenerating(false);
    }
  };
  
  const handleGenerateVariations = async (originalContent, count = 3) => {
    if (!originalContent) {
      toast.error('No content to generate variations for');
      return [];
    }
    
    try {
      setGenerating(true);
      const variations = [];
      
      const tones = ['professional', 'casual', 'urgent'];
      for (let i = 0; i < Math.min(count, 3); i++) {
        const res = await aiToolsAPI.refineCopy({
          content: originalContent,
          instruction: `Rewrite this content with a ${tones[i]} tone while keeping the core message intact.`,
          tone: tones[i],
        });
        
        if (res.data?.refined_content) {
          variations.push({
            tone: tones[i],
            content: res.data.refined_content,
          });
        }
      }
      
      return variations;
    } catch (error) {
      console.error('Failed to generate variations:', error);
      toast.error('Failed to generate variations');
      return [];
    } finally {
      setGenerating(false);
    }
  };
  
  const priorityColors = {
    critical: 'bg-red-500 text-white',
    high: 'bg-orange-500 text-white',
    medium: 'bg-yellow-500 text-white',
    low: 'bg-gray-400 text-white'
  };

  const getContentTypeInfo = (type) => {
    return CONTENT_TYPES.find(t => t.value === type) || CONTENT_TYPES[0];
  };

  const getStatusInfo = (status) => {
    return STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
  };

  const getTotalCount = () => {
    return Object.values(stats).reduce((acc, type) => acc + (type?.total || 0), 0);
  };

  return (
    <div className="space-y-6" data-testid="marketing-creative-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Palette className="h-6 w-6" />
            Creative Hub
          </h1>
          <p className="text-muted-foreground">
            Create and manage all your marketing content with AI assistance
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/marketing/asset-repository')}>
            <Image className="h-4 w-4 mr-2" />
            Asset Library
          </Button>
          <Button variant="outline" onClick={() => navigate('/marketing/templates')}>
            <Layers className="h-4 w-4 mr-2" />
            Templates
          </Button>
          <Button 
            variant="outline" 
            className="bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100"
            onClick={() => { resetAIStudio(); setShowAIStudioDialog(true); }}
          >
            <Wand2 className="h-4 w-4 mr-2" />
            AI Studio
          </Button>
          <Button onClick={() => { resetForm(); setShowCreateDialog(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            Create Content
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-10 gap-2">
        <Card 
          className={`cursor-pointer transition-colors ${activeTab === 'all' ? 'ring-2 ring-primary' : 'hover:bg-slate-50'}`}
          onClick={() => setActiveTab('all')}
        >
          <CardContent className="p-3 text-center">
            <div className="text-xl font-bold">{getTotalCount()}</div>
            <div className="text-xs text-muted-foreground">All</div>
          </CardContent>
        </Card>
        {CONTENT_TYPES.map(type => (
          <Card 
            key={type.value}
            className={`cursor-pointer transition-colors ${activeTab === type.value ? 'ring-2 ring-primary' : 'hover:bg-slate-50'}`}
            onClick={() => setActiveTab(type.value)}
          >
            <CardContent className="p-3 text-center">
              <type.icon className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
              <div className="text-lg font-bold">{stats[type.value]?.total || 0}</div>
              <div className="text-xs text-muted-foreground truncate">{type.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search content..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="content-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {STATUS_OPTIONS.map(s => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={fetchContent}>
          <RefreshCw className="h-4 w-4 mr-1" />
          Refresh
        </Button>
      </div>
      
      {/* Campaigns Section */}
      <Card>
        <CardHeader className="py-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Target className="h-5 w-5" />
              Active Campaigns
            </CardTitle>
            <Button size="sm" variant="outline" onClick={() => navigate('/marketing/campaigns')}>
              View All
            </Button>
          </div>
        </CardHeader>
        <CardContent className="py-2">
          <div className="flex flex-wrap gap-2">
            {campaigns.filter(c => ['live', 'scheduled', 'approved', 'pending_review', 'draft', 'active', 'planning'].includes(c.status)).slice(0, 6).map(campaign => (
              <div 
                key={campaign.id}
                className="flex items-center gap-2 px-3 py-2 border rounded-lg bg-background hover:bg-muted/50 cursor-pointer transition-colors"
                onClick={() => handleEditCampaign(campaign)}
                data-testid={`campaign-item-${campaign.id}`}
              >
                <div className={`w-2 h-2 rounded-full ${['live', 'active'].includes(campaign.status) ? 'bg-green-500' : 'bg-blue-500'}`} />
                <span className="text-sm font-medium">{campaign.name}</span>
                <Button variant="ghost" size="icon" className="h-6 w-6 ml-1" data-testid={`campaign-edit-${campaign.id}`}>
                  <Edit className="h-3 w-3" />
                </Button>
              </div>
            ))}
            {campaigns.filter(c => ['live', 'scheduled', 'approved', 'pending_review', 'draft', 'active', 'planning'].includes(c.status)).length === 0 && (
              <p className="text-sm text-muted-foreground py-2">No active campaigns</p>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Tasks Overview */}
      {contentTasks.length > 0 && (
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <ListTodo className="h-5 w-5" />
              Active Tasks ({contentTasks.filter(t => t.status !== 'completed').length})
            </CardTitle>
          </CardHeader>
          <CardContent className="py-2">
            <div className="space-y-2">
              {contentTasks.filter(t => t.status !== 'completed').slice(0, 5).map(task => {
                const assignee = teamMembers.find(m => m.id === task.assigned_to);
                return (
                  <div key={task.id} className="flex items-center justify-between p-2 border rounded-lg">
                    <div className="flex items-center gap-2">
                      <input 
                        type="checkbox"
                        checked={task.status === 'completed'}
                        onChange={(e) => handleUpdateTaskStatus(task.id, e.target.checked ? 'completed' : 'todo')}
                        className="h-4 w-4"
                      />
                      <div>
                        <p className="text-sm font-medium">{task.title}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          {task.content_title && <span>{task.content_title}</span>}
                          {assignee && <span>• {assignee.first_name} {assignee.last_name}</span>}
                        </div>
                      </div>
                    </div>
                    <Badge className={priorityColors[task.priority]}>{task.priority}</Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Content List */}
      {loading ? (
        <div className="flex items-center justify-center h-[40vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : content.length === 0 ? (
        <Card className="py-12">
          <CardContent className="text-center">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No Content Found</h3>
            <p className="text-muted-foreground mb-4">
              {search || statusFilter !== 'all' || activeTab !== 'all'
                ? 'Try adjusting your filters' 
                : 'Create your first piece of content to get started'}
            </p>
            <div className="flex items-center justify-center gap-2">
              <Button variant="outline" onClick={() => { resetAIStudio(); setShowAIStudioDialog(true); }}>
                <Wand2 className="h-4 w-4 mr-2" />
                Generate with AI
              </Button>
              <Button onClick={() => { resetForm(); setShowCreateDialog(true); }}>
                <Plus className="h-4 w-4 mr-2" />
                Create Content
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {content.map(item => {
            const typeInfo = getContentTypeInfo(item.content_type);
            const statusInfo = getStatusInfo(item.status);
            
            return (
              <Card 
                key={item.id} 
                className="hover:shadow-md transition-shadow"
                data-testid={`content-item-${item.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className={`p-3 rounded-lg ${typeInfo.color}`}>
                      <typeInfo.icon className="h-6 w-6" />
                    </div>

                    {/* Content Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium truncate">{item.title}</h3>
                        <Badge className={statusInfo.color} variant="secondary">
                          {statusInfo.label}
                        </Badge>
                        {item.ai_generated && (
                          <Badge variant="outline" className="bg-purple-50 text-purple-700">
                            <Sparkles className="h-3 w-3 mr-1" />
                            AI
                          </Badge>
                        )}
                      </div>
                      
                      {item.excerpt && (
                        <p className="text-sm text-muted-foreground line-clamp-1 mb-2">
                          {item.excerpt}
                        </p>
                      )}

                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="capitalize">{item.content_type?.replace('_', ' ')}</span>
                        {item.platform && (
                          <span className="flex items-center gap-1 capitalize">
                            <Share2 className="h-3 w-3" />
                            {item.platform}
                          </span>
                        )}
                        {item.publish_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(item.publish_date).toLocaleDateString()}
                          </span>
                        )}
                        {item.brand_voice_score && (
                          <span className={`flex items-center gap-1 ${item.brand_voice_score >= 70 ? 'text-green-600' : item.brand_voice_score >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                            <CheckCircle2 className="h-3 w-3" />
                            {item.brand_voice_score}% Brand Voice
                          </span>
                        )}
                        {item.campaign_ids?.length > 0 && (
                          <span className="flex items-center gap-1">
                            <Target className="h-3 w-3" />
                            {item.campaign_ids.length} Campaign(s)
                          </span>
                        )}
                      </div>

                      {/* Tags */}
                      {item.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {item.tags.slice(0, 5).map(tag => (
                            <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                          ))}
                          {item.tags.length > 5 && (
                            <Badge variant="outline" className="text-xs">+{item.tags.length - 5}</Badge>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => { setSelectedContent(item); setShowDetailDialog(true); }}
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => { setSelectedContent(item); setShowTaskDialog(true); }}
                        title="Create Task"
                      >
                        <ListTodo className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleAnalyzeContent(item.id)}
                        title="Analyze with AI"
                      >
                        <Sparkles className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="text-red-500 hover:text-red-600"
                        onClick={() => handleDeleteContent(item.id)}
                        title="Archive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* AI Studio Dialog */}
      <Dialog open={showAIStudioDialog} onOpenChange={setShowAIStudioDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="h-5 w-5 text-purple-600" />
              AI Content Studio
            </DialogTitle>
            <DialogDescription>
              Generate optimized marketing content with AI assistance
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-6 py-4">
            {/* Input Section */}
            <div className="space-y-4">
              <Card className="bg-purple-50 border-purple-200">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Lightbulb className="h-5 w-5 text-purple-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-purple-900">Refine Your Idea</h4>
                      <p className="text-sm text-purple-700">
                        Enter your concept and let AI transform it into polished content
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-2">
                <Label>Your Idea / Topic *</Label>
                <Textarea
                  value={aiInput.idea}
                  onChange={(e) => setAiInput({ ...aiInput, idea: e.target.value })}
                  placeholder="e.g., We're launching a new eco-friendly product line. I want to announce it with emphasis on sustainability and premium quality."
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Content Type</Label>
                  <Select 
                    value={aiInput.contentType} 
                    onValueChange={(v) => setAiInput({ ...aiInput, contentType: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTENT_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <type.icon className="h-4 w-4" />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {aiInput.contentType === 'social_media' && (
                  <div className="space-y-2">
                    <Label>Platform</Label>
                    <Select 
                      value={aiInput.platform} 
                      onValueChange={(v) => setAiInput({ ...aiInput, platform: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SOCIAL_PLATFORMS.map(p => (
                          <SelectItem key={p.value} value={p.value}>
                            <div className="flex items-center gap-2">
                              <p.icon className="h-4 w-4" />
                              {p.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {aiInput.contentType !== 'social_media' && (
                  <div className="space-y-2">
                    <Label>Length</Label>
                    <Select 
                      value={aiInput.length} 
                      onValueChange={(v) => setAiInput({ ...aiInput, length: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="short">Short (100-200 words)</SelectItem>
                        <SelectItem value="medium">Medium (300-500 words)</SelectItem>
                        <SelectItem value="long">Long (700-1000 words)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Target Audience</Label>
                <Input
                  value={aiInput.targetAudience}
                  onChange={(e) => setAiInput({ ...aiInput, targetAudience: e.target.value })}
                  placeholder="e.g., Eco-conscious millennials, ages 25-40"
                />
              </div>

              <div className="space-y-2">
                <Label>Tone</Label>
                <Select 
                  value={aiInput.tone} 
                  onValueChange={(v) => setAiInput({ ...aiInput, tone: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="friendly">Friendly & Conversational</SelectItem>
                    <SelectItem value="authoritative">Authoritative</SelectItem>
                    <SelectItem value="playful">Playful & Fun</SelectItem>
                    <SelectItem value="urgent">Urgent & Action-Oriented</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Target Keywords (comma-separated)</Label>
                <Input
                  value={aiInput.keywords}
                  onChange={(e) => setAiInput({ ...aiInput, keywords: e.target.value })}
                  placeholder="eco-friendly, sustainable, premium quality"
                />
              </div>

              <Button 
                onClick={handleGenerateWithAI} 
                disabled={generating || !aiInput.idea} 
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                {generating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate Content
                  </>
                )}
              </Button>
            </div>

            {/* Output Section */}
            <div className="space-y-4">
              {!aiOutput ? (
                <div className="h-full flex items-center justify-center border-2 border-dashed rounded-lg p-8">
                  <div className="text-center text-muted-foreground">
                    <Sparkles className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Generated content will appear here</p>
                    <p className="text-sm mt-2">Enter your idea and click Generate</p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Brand Voice Score */}
                  {brandVoiceScore && (
                    <Card className={`${brandVoiceScore.score >= 70 ? 'bg-green-50 border-green-200' : brandVoiceScore.score >= 50 ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200'}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium">Brand Voice Score</span>
                          <span className={`text-2xl font-bold ${brandVoiceScore.score >= 70 ? 'text-green-600' : brandVoiceScore.score >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                            {brandVoiceScore.score}%
                          </span>
                        </div>
                        <Progress value={brandVoiceScore.score} className="mb-2" />
                        {brandVoiceScore.feedback.length > 0 && (
                          <div className="space-y-1 mt-2">
                            {brandVoiceScore.feedback.slice(0, 3).map((fb, idx) => (
                              <div key={idx} className="flex items-center gap-2 text-sm">
                                {fb.type === 'positive' ? (
                                  <ThumbsUp className="h-3 w-3 text-green-600" />
                                ) : (
                                  <AlertCircle className="h-3 w-3 text-red-600" />
                                )}
                                <span>{fb.text}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* SEO Analysis */}
                  {seoAnalysis && aiInput.contentType !== 'social_media' && (
                    <Card>
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <TrendingUp className="h-4 w-4" />
                          SEO Analysis
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0 space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span>Word Count</span>
                          <Badge variant="outline">{seoAnalysis.wordCount}</Badge>
                        </div>
                        {Object.entries(seoAnalysis.keywordDensity).slice(0, 3).map(([kw, data]) => (
                          <div key={kw} className="flex items-center justify-between text-sm">
                            <span className="truncate max-w-[150px]">&quot;{kw}&quot;</span>
                            <Badge variant={data.optimal ? 'default' : 'outline'}>
                              {data.count}x ({data.density}%)
                            </Badge>
                          </div>
                        ))}
                        {seoAnalysis.suggestions.length > 0 && (
                          <div className="pt-2 border-t">
                            {seoAnalysis.suggestions.slice(0, 2).map((s, i) => (
                              <p key={i} className="text-xs text-amber-600 flex items-start gap-1">
                                <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                                {s}
                              </p>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Generated Content Preview */}
                  <Card>
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm">Generated Content</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0 space-y-3">
                      {aiOutput.title && (
                        <div>
                          <Label className="text-xs text-muted-foreground">Title</Label>
                          <p className="font-medium">{aiOutput.title}</p>
                        </div>
                      )}
                      {aiOutput.subject_line && (
                        <div>
                          <Label className="text-xs text-muted-foreground">Subject Line</Label>
                          <p className="font-medium">{aiOutput.subject_line}</p>
                        </div>
                      )}
                      {(aiOutput.body || aiOutput.content) && (
                        <div>
                          <Label className="text-xs text-muted-foreground">Content</Label>
                          <div className="p-3 bg-slate-50 rounded-lg max-h-[200px] overflow-y-auto text-sm">
                            {aiOutput.body || aiOutput.content}
                          </div>
                        </div>
                      )}
                      {aiOutput.hashtags && (
                        <div>
                          <Label className="text-xs text-muted-foreground">Hashtags</Label>
                          <p className="text-sm text-blue-600">{aiOutput.hashtags.join(' ')}</p>
                        </div>
                      )}
                      {aiOutput.cta && (
                        <div>
                          <Label className="text-xs text-muted-foreground">Call to Action</Label>
                          <Badge variant="default">{aiOutput.cta}</Badge>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={handleGenerateWithAI}
                      disabled={generating}
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Regenerate
                    </Button>
                    <Button 
                      className="flex-1"
                      onClick={applyAIContent}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Use This Content
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Content Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Marketing Content</DialogTitle>
            <DialogDescription>
              Create new content manually or use AI-generated content
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Content Type</Label>
                <Select 
                  value={formData.content_type} 
                  onValueChange={(v) => setFormData({ ...formData, content_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTENT_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          <type.icon className="h-4 w-4" />
                          {type.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Enter content title"
                  data-testid="content-title-input"
                />
              </div>
            </div>

            {/* Social Media Specific Fields */}
            {formData.content_type === 'social_media' && (
              <div className="grid grid-cols-2 gap-4 p-4 bg-purple-50 rounded-lg">
                <div className="space-y-2">
                  <Label>Platform</Label>
                  <Select 
                    value={formData.platform} 
                    onValueChange={(v) => setFormData({ ...formData, platform: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SOCIAL_PLATFORMS.map(p => (
                        <SelectItem key={p.value} value={p.value}>
                          <div className="flex items-center gap-2">
                            <p.icon className="h-4 w-4" />
                            {p.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Hashtags</Label>
                  <Input
                    value={formData.hashtags}
                    onChange={(e) => setFormData({ ...formData, hashtags: e.target.value })}
                    placeholder="#marketing #business #growth"
                  />
                </div>
              </div>
            )}

            {/* Email Specific Fields */}
            {formData.content_type === 'email' && (
              <div className="space-y-4 p-4 bg-rose-50 rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email Type</Label>
                    <Select 
                      value={formData.email_type} 
                      onValueChange={(v) => setFormData({ ...formData, email_type: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {EMAIL_TYPES.map(t => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Subject Line</Label>
                    <Input
                      value={formData.subject_line}
                      onChange={(e) => setFormData({ ...formData, subject_line: e.target.value })}
                      placeholder="Your email subject"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Preview Text</Label>
                  <Input
                    value={formData.preview_text}
                    onChange={(e) => setFormData({ ...formData, preview_text: e.target.value })}
                    placeholder="Preview text shown in inbox"
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Excerpt / Summary</Label>
              <Textarea
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                placeholder="Short summary of the content..."
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Body Content</Label>
              <Textarea
                value={formData.body}
                onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                placeholder="Main content body (HTML or Markdown supported)..."
                rows={6}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Meta Title</Label>
                <Input
                  value={formData.meta_title}
                  onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                  placeholder="SEO title"
                />
              </div>
              <div className="space-y-2">
                <Label>Featured Image URL</Label>
                <Input
                  value={formData.featured_image_url}
                  onChange={(e) => setFormData({ ...formData, featured_image_url: e.target.value })}
                  placeholder="https://..."
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Meta Description</Label>
              <Textarea
                value={formData.meta_description}
                onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                placeholder="SEO description (150-160 characters)"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Keywords (comma-separated)</Label>
                <Input
                  value={formData.keywords}
                  onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                  placeholder="seo, marketing"
                />
              </div>
              <div className="space-y-2">
                <Label>Categories</Label>
                <Input
                  value={formData.categories}
                  onChange={(e) => setFormData({ ...formData, categories: e.target.value })}
                  placeholder="Blog, How-To"
                />
              </div>
              <div className="space-y-2">
                <Label>Tags</Label>
                <Input
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="featured, trending"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Link to Campaign</Label>
              <Select 
                value={formData.campaign_ids[0] || 'none'} 
                onValueChange={(v) => setFormData({ ...formData, campaign_ids: v && v !== 'none' ? [v] : [] })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a campaign (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {campaigns.map(campaign => (
                    <SelectItem key={campaign.id} value={campaign.id}>
                      {campaign.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateContent} disabled={creating}>
              {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Content
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Content Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedContent?.title}</DialogTitle>
            <DialogDescription>
              {selectedContent?.content_type?.replace('_', ' ')} • Created {selectedContent?.created_at && new Date(selectedContent.created_at).toLocaleDateString()}
            </DialogDescription>
          </DialogHeader>

          {selectedContent && (
            <div className="space-y-4 py-4">
              {/* Status & Info */}
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={getStatusInfo(selectedContent.status).color}>
                  {getStatusInfo(selectedContent.status).label}
                </Badge>
                {selectedContent.brand_voice_score && (
                  <Badge variant="outline" className={`${selectedContent.brand_voice_score >= 70 ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    {selectedContent.brand_voice_score}% Brand Voice
                  </Badge>
                )}
                {selectedContent.ai_generated && (
                  <Badge variant="outline" className="bg-purple-50 text-purple-700">
                    <Sparkles className="h-3 w-3 mr-1" />
                    AI Generated
                  </Badge>
                )}
                {selectedContent.platform && (
                  <Badge variant="outline">
                    <Share2 className="h-3 w-3 mr-1" />
                    {selectedContent.platform}
                  </Badge>
                )}
              </div>

              {/* Featured Image */}
              {selectedContent.featured_image_url && (
                <div className="aspect-video bg-slate-100 rounded-lg overflow-hidden">
                  <img 
                    src={selectedContent.featured_image_url} 
                    alt={selectedContent.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              {/* Subject Line for Email */}
              {selectedContent.subject_line && (
                <div>
                  <Label className="text-muted-foreground">Subject Line</Label>
                  <p className="mt-1 font-medium">{selectedContent.subject_line}</p>
                </div>
              )}

              {/* Excerpt */}
              {selectedContent.excerpt && (
                <div>
                  <Label className="text-muted-foreground">Excerpt</Label>
                  <p className="mt-1">{selectedContent.excerpt}</p>
                </div>
              )}

              {/* Body */}
              {selectedContent.body && (
                <div>
                  <Label className="text-muted-foreground">Content</Label>
                  <div 
                    className="mt-1 prose prose-sm max-w-none p-3 bg-slate-50 rounded-lg max-h-[300px] overflow-y-auto"
                    dangerouslySetInnerHTML={{ __html: selectedContent.body }}
                  />
                </div>
              )}

              {/* Hashtags for Social */}
              {selectedContent.hashtags && (
                <div>
                  <Label className="text-muted-foreground">Hashtags</Label>
                  <p className="mt-1 text-blue-600">{selectedContent.hashtags}</p>
                </div>
              )}

              {/* Meta */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                {selectedContent.meta_title && (
                  <div>
                    <Label className="text-muted-foreground">Meta Title</Label>
                    <p className="mt-1">{selectedContent.meta_title}</p>
                  </div>
                )}
                {selectedContent.meta_description && (
                  <div>
                    <Label className="text-muted-foreground">Meta Description</Label>
                    <p className="mt-1">{selectedContent.meta_description}</p>
                  </div>
                )}
              </div>

              {/* Tags & Keywords */}
              <div className="flex flex-wrap gap-4">
                {selectedContent.tags?.length > 0 && (
                  <div>
                    <Label className="text-muted-foreground text-xs">Tags</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedContent.tags.map(tag => (
                        <Badge key={tag} variant="secondary">{tag}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {selectedContent.keywords?.length > 0 && (
                  <div>
                    <Label className="text-muted-foreground text-xs">Keywords</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedContent.keywords.map(kw => (
                        <Badge key={kw} variant="outline">{kw}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* AI Suggestions */}
              {selectedContent.ai_suggestions?.length > 0 && (
                <div className="p-3 bg-purple-50 rounded-lg">
                  <h4 className="font-medium flex items-center gap-2 mb-2">
                    <Sparkles className="h-4 w-4 text-purple-600" />
                    AI Suggestions
                  </h4>
                  <ul className="space-y-1 text-sm">
                    {selectedContent.ai_suggestions.map((suggestion, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-purple-600">•</span>
                        <span>{suggestion.suggestion || suggestion}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-2 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => handleAnalyzeContent(selectedContent.id)}
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  Re-Analyze
                </Button>
                
                <Select onValueChange={(campaignId) => {
                  contentAPI.addToCampaign(selectedContent.id, campaignId);
                  toast.success('Added to campaign');
                }}>
                  <SelectTrigger className="w-[200px]">
                    <Target className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Add to Campaign" />
                  </SelectTrigger>
                  <SelectContent>
                    {campaigns.map(campaign => (
                      <SelectItem key={campaign.id} value={campaign.id}>
                        {campaign.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Task Assignment Dialog */}
      <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListTodo className="h-5 w-5" />
              Create Task
            </DialogTitle>
            <DialogDescription>
              Assign a task to team members for this content
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Bug #9: AI Task Assistant Button */}
            <Button 
              variant="outline" 
              className="w-full border-dashed border-violet-300 text-violet-700 hover:bg-violet-50"
              onClick={handleAISuggestTask}
              disabled={aiSuggestingTask}
              data-testid="ai-suggest-task-btn"
            >
              {aiSuggestingTask ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  AI is thinking...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  AI Suggest Task Details
                </>
              )}
            </Button>
            <div>
              <Label htmlFor="task-title">Title *</Label>
              <Input
                id="task-title"
                value={taskForm.title}
                onChange={(e) => setTaskForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Review and approve content"
              />
            </div>
            <div>
              <Label htmlFor="task-desc">Description</Label>
              <Textarea
                id="task-desc"
                value={taskForm.description}
                onChange={(e) => setTaskForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Task details..."
                rows={3}
              />
            </div>
            <div>
              <Label>Assign To</Label>
              <Select 
                value={taskForm.assigned_to || 'unassigned'} 
                onValueChange={(v) => setTaskForm(prev => ({ ...prev, assigned_to: v === 'unassigned' ? '' : v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select team member" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {teamMembers.map(member => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.first_name} {member.last_name} ({member.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Priority</Label>
                <Select 
                  value={taskForm.priority} 
                  onValueChange={(v) => setTaskForm(prev => ({ ...prev, priority: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="task-due">Due Date</Label>
                <Input
                  id="task-due"
                  type="date"
                  value={taskForm.due_date}
                  onChange={(e) => setTaskForm(prev => ({ ...prev, due_date: e.target.value }))}
                />
              </div>
            </div>
            {selectedContent && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm font-medium">Linked Content:</p>
                <p className="text-sm text-muted-foreground">{selectedContent.title}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTaskDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateTask} disabled={!taskForm.title}>
              <Plus className="h-4 w-4 mr-1" />
              Create Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Campaign Edit Dialog */}
      <Dialog open={showCampaignDialog} onOpenChange={setShowCampaignDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              Edit Campaign
            </DialogTitle>
            <DialogDescription>
              {selectedCampaign?.name}
            </DialogDescription>
          </DialogHeader>
          {selectedCampaign && (
            <Tabs value={campaignTab} onValueChange={setCampaignTab} className="mt-4">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="content">Content</TabsTrigger>
                <TabsTrigger value="tasks">Tasks</TabsTrigger>
              </TabsList>
              
              <TabsContent value="overview" className="mt-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Status</Label>
                    <Select 
                      value={selectedCampaign.status}
                      onValueChange={(v) => setSelectedCampaign(prev => ({ ...prev, status: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="paused">Paused</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Priority</Label>
                    <Select 
                      value={selectedCampaign.priority || 'medium'}
                      onValueChange={(v) => setSelectedCampaign(prev => ({ ...prev, priority: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="critical">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={selectedCampaign.description || ''}
                    onChange={(e) => setSelectedCampaign(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Budget</Label>
                    <Input
                      type="number"
                      value={selectedCampaign.budget || 0}
                      onChange={(e) => setSelectedCampaign(prev => ({ ...prev, budget: parseFloat(e.target.value) || 0 }))}
                    />
                  </div>
                  <div>
                    <Label>Target Audience</Label>
                    <Input
                      value={selectedCampaign.target_audience || ''}
                      onChange={(e) => setSelectedCampaign(prev => ({ ...prev, target_audience: e.target.value }))}
                    />
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="content" className="mt-4">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <p className="text-sm font-medium">Associated Content</p>
                    <Button size="sm" variant="outline" onClick={() => setShowCreateDialog(true)}>
                      <Plus className="h-4 w-4 mr-1" />
                      Add Content
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {content.filter(c => c.campaign_ids?.includes(selectedCampaign.id)).map(item => {
                      const typeInfo = getContentTypeInfo(item.content_type);
                      return (
                        <div key={item.id} className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="flex items-center gap-2">
                            <typeInfo.icon className="h-4 w-4" />
                            <span className="font-medium">{item.title}</span>
                            <Badge variant="secondary">{getStatusInfo(item.status).label}</Badge>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => { setSelectedContent(item); setShowDetailDialog(true); }}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      );
                    })}
                    {content.filter(c => c.campaign_ids?.includes(selectedCampaign.id)).length === 0 && (
                      <p className="text-center text-muted-foreground py-6">No content linked to this campaign</p>
                    )}
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="tasks" className="mt-4">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <p className="text-sm font-medium">Campaign Tasks</p>
                    <Button size="sm" onClick={() => setShowTaskDialog(true)}>
                      <Plus className="h-4 w-4 mr-1" />
                      Add Task
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {contentTasks.filter(t => t.campaign_id === selectedCampaign?.id || !t.campaign_id).map(task => {
                      const assignee = teamMembers.find(m => m.id === task.assigned_to);
                      return (
                        <div key={task.id} className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="flex items-center gap-3">
                            <input 
                              type="checkbox"
                              checked={task.status === 'completed'}
                              onChange={(e) => handleUpdateTaskStatus(task.id, e.target.checked ? 'completed' : 'todo')}
                              className="h-4 w-4"
                            />
                            <div>
                              <p className={`font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                                {task.title}
                              </p>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                {task.due_date && (
                                  <span className="flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    {new Date(task.due_date).toLocaleDateString()}
                                  </span>
                                )}
                                {assignee && (
                                  <span className="flex items-center gap-1">
                                    <Users className="h-3 w-3" />
                                    {assignee.first_name} {assignee.last_name}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <Badge className={priorityColors[task.priority]}>{task.priority}</Badge>
                        </div>
                      );
                    })}
                    {contentTasks.length === 0 && (
                      <p className="text-center text-muted-foreground py-6">No tasks yet</p>
                    )}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCampaignDialog(false)}>Cancel</Button>
            <Button onClick={() => handleUpdateCampaign(selectedCampaign)}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MarketingCreativePage;
